import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { dashboardApi, productsApi, categoriesApi } from "../lib/api";
import { useAuth } from "./AuthContext";

const AdminContext = createContext(null);

export const AdminProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    total_products: 0,
    total_categories: 0,
    active_products: 0,
    out_of_stock_products: 0,
    latest_products: []
  });
  const [dashboardFilters, setDashboardFilters] = useState({
    period: "last_30_days",
  });
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load dashboard data
  const loadDashboardData = useCallback(async (filters = dashboardFilters) => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardApi.getSummary(filters);
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, dashboardFilters]);

  // Load products for admin
  const loadProducts = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const data = await productsApi.getAllAdmin();
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      setError(error.message);
    }
  }, [isAuthenticated]);

  // Load categories for admin
  const loadCategories = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const data = await categoriesApi.getAllAdmin();
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError(error.message);
    }
  }, [isAuthenticated]);

  // Load all data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadDashboardData(dashboardFilters);
      loadProducts();
      loadCategories();
    } else {
      // Reset data when not authenticated
      setDashboardData({
        total_products: 0,
        total_categories: 0,
        active_products: 0,
        out_of_stock_products: 0,
        latest_products: []
      });
      setProducts([]);
      setCategories([]);
      setLoading(false);
    }
  }, [isAuthenticated, loadDashboardData, loadProducts, loadCategories, dashboardFilters]);

  // Product management functions
  const createProduct = useCallback(async (productData) => {
    try {
      const newProduct = await productsApi.create(productData);
      await loadProducts(); // Reload products
      await loadDashboardData(); // Reload dashboard
      return newProduct;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }, [loadProducts, loadDashboardData]);

  const updateProduct = useCallback(async (id, productData) => {
    try {
      const updatedProduct = await productsApi.update(id, productData);
      await loadProducts(); // Reload products
      await loadDashboardData(); // Reload dashboard
      return updatedProduct;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }, [loadProducts, loadDashboardData]);

  const deleteProduct = useCallback(async (id) => {
    try {
      await productsApi.delete(id);
      await loadProducts(); // Reload products
      await loadDashboardData(); // Reload dashboard
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }, [loadProducts, loadDashboardData]);

  const uploadProductImage = useCallback(async (file) => {
    return productsApi.uploadImage(file);
  }, []);

  // Category management functions
  const createCategory = useCallback(async (categoryData) => {
    try {
      const newCategory = await categoriesApi.create(categoryData);
      await loadCategories(); // Reload categories
      await loadDashboardData(); // Reload dashboard
      return newCategory;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }, [loadCategories, loadDashboardData]);

  const updateCategory = useCallback(async (id, categoryData) => {
    try {
      const updatedCategory = await categoriesApi.update(id, categoryData);
      await loadCategories(); // Reload categories
      await loadDashboardData(); // Reload dashboard
      return updatedCategory;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }, [loadCategories, loadDashboardData]);

  const deleteCategory = useCallback(async (id) => {
    try {
      await categoriesApi.delete(id);
      await loadCategories(); // Reload categories
      await loadDashboardData(); // Reload dashboard
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }, [loadCategories, loadDashboardData]);

  // Refresh all data
  const refreshData = useCallback(async () => {
    await Promise.all([
      loadDashboardData(dashboardFilters),
      loadProducts(),
      loadCategories()
    ]);
  }, [loadDashboardData, loadProducts, loadCategories, dashboardFilters]);

  const value = {
    // Data
    dashboardData,
    products,
    categories,
    loading,
    error,
    
    // Actions
    refreshData,
    dashboardFilters,
    setDashboardFilters,
    
    // Product actions
    createProduct,
    updateProduct,
    deleteProduct,
    uploadProductImage,
    
    // Category actions
    createCategory,
    updateCategory,
    deleteCategory,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};
